import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import { useFirestoreRealtime } from "../../hooks/useFirestoreRealtime";

interface RealtimeAuditTableProps {
  collectionName: string;
  title: string;
  columns: {
    field: string;
    header: string;
    sortable?: boolean;
    filter?: boolean;
    render?: (value: any, row: any) => React.ReactNode;
  }[];
  actions?: {
    label: string;
    icon: string;
    onClick: (row: any) => void;
    severity?: "secondary" | "success" | "info" | "danger" | "warning" | "help";
    outlined?: boolean;
    disabled?: (row: any) => boolean;
  }[];
  limitCount?: number; // Default to 50 for latest entries
  showNewIndicator?: boolean;
  orderByField?: string;
  orderDirection?: "asc" | "desc";
}

const RealtimeAuditTable: React.FC<RealtimeAuditTableProps> = ({
  collectionName,
  title,
  columns,
  actions = [],
  limitCount = 50, // Default to 50 latest entries
  showNewIndicator = true,
  orderByField = "timestamp",
  orderDirection = "desc",
}) => {
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  const {
    data: auditData,
    loading,
    error,
    lastUpdated,
    connectionStatus,
    refresh,
  } = useFirestoreRealtime({
    collectionName,
    orderByField,
    orderDirection,
    limitCount,
    enableDebugLogging: true,
    enablePagination: false, // Always use real-time mode
  });

  const formatLastUpdated = (date: Date | null) => {
    if (!date) return "";
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  const isNewIssue = (row: any) => {
    if (!lastUpdated || !showNewIndicator) return false;
    // Check if the row was added in the last 5 minutes
    // Handle both timestamp and updatedAt fields
    const timestampField = row.timestamp || row.updatedAt;
    if (!timestampField) return false;

    const rowTimestamp =
      timestampField.toDate?.() ||
      new Date(timestampField._seconds * 1000 || timestampField);
    return Date.now() - rowTimestamp.getTime() < 300000; // 5 minutes
  };

  const renderColumn = (column: any, row: any) => {
    if (column.render) {
      return column.render(row[column.field], row);
    }

    const value = row[column.field];

    // Handle different data types
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    if (typeof value === "number") {
      return value.toLocaleString();
    }

    if (Array.isArray(value)) {
      return value.join(", ");
    }

    return value || "-";
  };

  const actionBodyTemplate = (row: any) => (
    <div className="flex space-x-2">
      {actions.map((action, index) => (
        <Button
          key={index}
          label={action.label}
          icon={action.icon}
          size="small"
          severity={action.severity || "secondary"}
          outlined={action.outlined}
          disabled={action.disabled ? action.disabled(row) : false}
          onClick={() => action.onClick(row)}
        />
      ))}
    </div>
  );

  const renderRefreshButton = () => {
    return (
      <div className="flex justify-end items-center mb-4">
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          onClick={refresh}
          disabled={loading}
          outlined
          size="small"
        />
      </div>
    );
  };

  if (error) {
    return (
      <Card className="mt-4">
        <div className="text-center text-red-600">
          <i className="pi pi-exclamation-triangle text-2xl mb-2"></i>
          <p>Error loading data: {error}</p>
          <Button
            label="Retry"
            icon="pi pi-refresh"
            onClick={refresh}
            className="mt-2"
          />
        </div>
      </Card>
    );
  }

  return (
    <Card className="mt-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold">{title}</h2>
          {lastUpdated && (
            <p className="text-sm text-gray-500">
              Last updated: {formatLastUpdated(lastUpdated)}
            </p>
          )}
        </div>

        {/* Connection status indicator */}
        <div className="flex items-center space-x-2">
          <div
            className={`w-2 h-2 rounded-full ${
              connectionStatus === "connected"
                ? "bg-green-500 animate-pulse"
                : connectionStatus === "connecting"
                ? "bg-yellow-500 animate-pulse"
                : "bg-red-500"
            }`}
          ></div>
          <span
            className={`text-sm ${
              connectionStatus === "connected"
                ? "text-green-600"
                : connectionStatus === "connecting"
                ? "text-yellow-600"
                : "text-red-600"
            }`}
          >
            {connectionStatus === "connected"
              ? "Live"
              : connectionStatus === "connecting"
              ? "Connecting"
              : "Error"}
          </span>
        </div>
      </div>

      {renderRefreshButton()}

      {loading && auditData.length === 0 ? (
        <div className="text-center py-8">
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
          <p className="mt-2 text-gray-500">Loading data...</p>
        </div>
      ) : (
        <>
          <DataTable
            value={auditData}
            loading={loading}
            paginator={true}
            rows={25}
            rowsPerPageOptions={[10, 25, 50]}
            selection={selectedRows}
            onSelectionChange={(e) => setSelectedRows(e.value)}
            dataKey="id"
            filterDisplay="menu"
            globalFilterFields={columns.map((col) => col.field)}
            emptyMessage="No data found"
            className="p-datatable-sm"
          >
            {/* New indicator column */}
            {showNewIndicator && (
              <Column
                header=""
                body={(row) =>
                  isNewIssue(row) ? (
                    <Badge value="NEW" severity="success" />
                  ) : null
                }
                style={{ width: "60px" }}
              />
            )}

            {/* Dynamic columns */}
            {columns.map((column, index) => (
              <Column
                key={index}
                field={column.field}
                header={column.header}
                sortable={column.sortable !== false}
                filter={column.filter !== false}
                body={(row) => renderColumn(column, row)}
                style={{ minWidth: "120px" }}
              />
            ))}

            {/* Actions column */}
            {actions.length > 0 && (
              <Column
                header="Actions"
                body={actionBodyTemplate}
                style={{ width: "200px" }}
              />
            )}
          </DataTable>
        </>
      )}

      {/* Summary footer */}
      <div className="mt-4 text-sm text-gray-600">
        <div className="flex justify-between items-center">
          <span>
            Showing latest {auditData.length} entries
            {selectedRows.length > 0 && ` • Selected: ${selectedRows.length}`}
          </span>
          <span className="text-green-600">Real-time updates enabled</span>
        </div>
      </div>
    </Card>
  );
};

export default RealtimeAuditTable;
